'use client';
import {
  createContext,
  useContext,
  useEffect,
  PropsWithChildren,
  useState,
} from 'react';
import { useUserContext } from './UserContext';
import {
  SchedulePreferences,
  StudySchedule,
  ScheduleChecklistItem,
  ScheduleSubject,
  ScheduleStatus,
} from '@osmosis/shared/types/schedule';
import { track } from '../util/tracking';

type ScheduleData = {
  aiSyllabusDisplayMatchCount?: number;
  scheduleID?: string;
  scheduleName?: string;
  studyStage?: string;
  scheduleType: string | null;
  scheduleTopics: ScheduleSubject[];
  startDate: Date | null;
  scheduleDate: Date | null;
  scheduleResources: string[];
  scheduleChecklist: ScheduleChecklistItem[];
  scheduleStudyHours: number[];
  scheduleStatus?: ScheduleStatus;
  templateID?: SchedulePreferences['templateID'];
  color?: SchedulePreferences['color'];
};

type ScheduleCreateContextProps = {
  data: ScheduleData;
  playlistID?: number;
  status?: 'idle' | 'loading' | 'success' | 'error';
  setScheduleID: (value: string) => void;
  setAiSyllabusDisplayMatchCount: (value: number | undefined) => void;
  setStudyStage: (value: string) => void;
  setScheduleType: (value: string | null) => void;
  setScheduleName: (value: string) => void;
  setScheduleTopics: (topics: ScheduleSubject[]) => void;
  setScheduleDate: (date: Date | null) => void;
  setScheduleResources: (resources: string[]) => void;
  setScheduleChecklist: (checklist: ScheduleChecklistItem[]) => void;
  setScheduleStatus: (status: ScheduleStatus) => void;
  setScheduleStudyHours: (studyHours: number[]) => void;
  setTemplateID?: (value: SchedulePreferences['templateID']) => void;
  setScheduleColor: (value: SchedulePreferences['color']) => void;
  resetScheduleData: () => void;
  submitSchedule: () => Promise<void>;
  activeScheduleID: string | null;
  setActiveScheduleID: (scheduleID: string) => void;
};

const initialScheduleCreateData: ScheduleData = {
  scheduleType: null,
  scheduleTopics: [],
  startDate: null,
  scheduleDate: null,
  scheduleResources: [],
  scheduleChecklist: [],
  scheduleStudyHours: [],
};

const DEFAULT_COLOR = 'blue';

export const ScheduleCreateContext = createContext<
  ScheduleCreateContextProps | undefined
>(undefined);

/**
 * ScheduleCreateProvider.
 */
export const ScheduleCreateProvider = ({
  schedules,
  playlistID,
  children,
}: PropsWithChildren<{
  schedules?: StudySchedule[];
  playlistID?: number;
}>) => {
  const [data, setData] = useState<ScheduleData>(initialScheduleCreateData);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const [activeScheduleID, setActiveScheduleID] = useState<string | null>(
    schedules && schedules.length > 0
      ? schedules[0].scheduleID.toString()
      : null,
  );

  const { user } = useUserContext();
  const { userID = 0 } = user || {};

  function getScheduleDataFromSchedule(schedule: StudySchedule) {
    return {
      ...initialScheduleCreateData,
      scheduleID: schedule.scheduleID.toString(),
      scheduleName: schedule.name,
      scheduleType: schedule.preferences.type || '',
      scheduleTopics: schedule.subjects,
      startDate: new Date(schedule.startDate || ''),
      scheduleDate: new Date(schedule.endDate || ''),
      studyStage: schedule.preferences.stage || '',
      scheduleResources: schedule.resources,
      scheduleStudyHours: schedule.preferences.dailyHours.map(
        (dailyHours: { day: number; hours: number }) => dailyHours.hours,
      ),
      scheduleStatus: schedule.status,
      templateID: schedule.preferences.templateID,
      color: schedule.preferences.color,
    };
  }

  useEffect(() => {
    if (schedules && activeScheduleID) {
      const schedule = schedules.find(
        (s) => s.scheduleID.toString() === activeScheduleID,
      );
      if (schedule) {
        setData(getScheduleDataFromSchedule(schedule));
      }
    }
  }, [schedules, activeScheduleID]);

  useEffect(() => {
    if (playlistID) setScheduleType('class-exam');
  }, [playlistID]);

  const setScheduleID = (value: string) => {
    setData((prev) => ({ ...prev, scheduleID: value }));
    setActiveScheduleID(value);
  };

  const setAiSyllabusDisplayMatchCount = (value: number | undefined) => {
    setData((prev) => ({ ...prev, aiSyllabusDisplayMatchCount: value }));
  };

  const setScheduleName = (value: string) => {
    setData((prev) => ({ ...prev, scheduleName: value }));
  };

  const setStudyStage = (value: string) => {
    setData((prev) => ({ ...prev, studyStage: value }));
  };

  const setScheduleType = (value: string | null) => {
    setData((prev) => ({ ...prev, scheduleType: value }));
  };

  const setScheduleTopics = (topics: ScheduleSubject[]) => {
    setData((prev) => ({ ...prev, scheduleTopics: topics }));
  };

  const setScheduleDate = (date: Date | null) => {
    setData((prev) => ({ ...prev, scheduleDate: date }));
  };

  const setScheduleResources = (resources: string[]) => {
    setData((prev) => ({ ...prev, scheduleResources: resources }));
  };

  const setScheduleChecklist = (checklist: ScheduleChecklistItem[]) => {
    setData((prev) => ({ ...prev, scheduleChecklist: checklist }));
  };

  const setScheduleStudyHours = (studyHours: number[]) => {
    setData((prev) => ({ ...prev, scheduleStudyHours: studyHours }));
  };

  const setScheduleStatus = (status: ScheduleStatus) => {
    setData((prev) => ({ ...prev, scheduleStatus: status }));
  };

  const setTemplateID = (templateID: SchedulePreferences['templateID']) => {
    setData((prev) => ({ ...prev, templateID }));
  };

  const setScheduleColor = (color: SchedulePreferences['color']) => {
    setData((prev) => ({ ...prev, color }));
  };

  const resetScheduleData = () => {
    if (schedules && activeScheduleID) {
      const schedule = schedules.find(
        (s) => s.scheduleID.toString() === activeScheduleID,
      );
      if (schedule) {
        setData(getScheduleDataFromSchedule(schedule));
        return;
      }
    }
    setData(initialScheduleCreateData);
  };

  const submitSchedule = async () => {
    setStatus('loading');
    try {
      const requestData = {
        startDate: new Date(),
        endDate: data.scheduleDate ? data.scheduleDate : null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        preferences: {
          dailyHours: data.scheduleStudyHours.map((hours, day) => ({
            day,
            hours,
          })),
          type: data.scheduleType,
          stage: data.studyStage,
          templateID: data.templateID,
          color: data.color || DEFAULT_COLOR,
        },
        checklist: !schedules ? data.scheduleChecklist : undefined,
        subjects: data.scheduleTopics,
        name: data.scheduleName || 'Study schedule',
        userID,
        resources: data.scheduleResources.filter(
          (resource) => resource !== 'osmosis',
        ),
        playlistID,
      };
      await fetch(`/api/schedule${!schedules ? '' : `/${data.scheduleID}`}`, {
        method: !schedules ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      setStatus('success');
      if (!activeScheduleID) {
        track('create_schedule', {
          status: 1,
        });
      } else {
        track('schedule_engagement', {
          scheduleID: activeScheduleID,
          type: 'update',
        });
      }
    } catch (error) {
      setStatus('error');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <ScheduleCreateContext.Provider
      value={{
        data,
        status,
        playlistID,
        setScheduleID,
        setAiSyllabusDisplayMatchCount,
        setStudyStage,
        setScheduleType,
        setScheduleName,
        setScheduleTopics,
        setScheduleDate,
        setScheduleResources,
        setScheduleChecklist,
        resetScheduleData,
        setScheduleStudyHours,
        setScheduleStatus,
        setTemplateID,
        setScheduleColor,
        submitSchedule,
        activeScheduleID,
        setActiveScheduleID,
      }}
    >
      {children}
    </ScheduleCreateContext.Provider>
  );
};

/**
 * Returns values of useScheduleCreateContext.
 */
export const useScheduleCreateContext = (): ScheduleCreateContextProps => {
  const context = useContext(ScheduleCreateContext);
  if (!context) {
    throw new Error(
      'useScheduleCreateContext must be used within a ScheduleCreateProvider',
    );
  }
  return context;
};
